import { useState, useEffect, useRef } from "react";
import { X, Printer, Download, Send, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
}

const ROLE_PILL = {
  SA: { label: "Accountant", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  CM: { label: "CM Manager", cls: "bg-teal-100 text-teal-700 border-teal-200" },
};

export default function AccountCardDrawer({
  cardId,
  boardKey,           // "accounting" | "cm_accounts"
  onClose,
  onUpdated,          // () => void  refresh parent list
  currentRole,
  availableStatuses = [],  // list of column names from parent board
}) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const notesEndRef = useRef(null);

  // Load report PDF as same-origin blob URL to bypass nested-iframe CSP.
  useEffect(() => {
    if (!previewOpen || !card?.completion_report_url || previewBlobUrl) return;
    let cancelled = false;
    let url = null;
    api.get(`/files/${card.completion_report_url}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setPreviewBlobUrl(url);
      })
      .catch((e) => !cancelled && setPreviewError(e.response?.data?.detail || "Failed to load PDF"));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewOpen, card?.completion_report_url, previewBlobUrl]);

  useEffect(() => () => { if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl); }, [previewBlobUrl]);

  const statusField = boardKey === "accounting" ? "accounting_status" : "cm_status";

  const changeStatus = async (newStatus) => {
    if (!newStatus || !card || newStatus === card[statusField]) return;
    setSavingStatus(true);
    try {
      const url = boardKey === "accounting"
        ? `/accounting/cards/${cardId}/status`
        : `/cm/accounts/cards/${cardId}/status`;
      await api.put(url, { status: newStatus });
      setCard((c) => ({ ...c, [statusField]: newStatus }));
      toast.success(`Status changed to ${newStatus}`);
      onUpdated && onUpdated();
      window.dispatchEvent(new CustomEvent("accounting-changed"));
      window.dispatchEvent(new CustomEvent("cm-accounts-changed"));
      // If newly Captured / archived, close drawer so the user sees the card disappear
      if (boardKey === "accounting" && newStatus === "Captured") {
        setTimeout(() => onClose(), 600);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change status");
    } finally { setSavingStatus(false); }
  };

  useEffect(() => {
    if (!cardId) return;
    (async () => {
      setLoading(true);
      try {
        const url = boardKey === "accounting"
          ? `/accounting/cards/${cardId}`
          : `/cm/accounts/cards/${cardId}`;
        const { data } = await api.get(url);
        setCard(data);
        // Tell parent + sidebar to refresh counts (unread was cleared for this user)
        onUpdated && onUpdated();
        window.dispatchEvent(new CustomEvent(
          boardKey === "accounting" ? "accounting-changed" : "cm-accounts-changed"
        ));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load record");
        onClose();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, boardKey]);

  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [card?.accounting_notes?.length]);

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setSending(true);
    try {
      // Notes endpoint is on the accounting namespace (shared for both roles)
      const { data } = await api.post(`/accounting/cards/${cardId}/notes`, { message: noteText.trim() });
      setCard((c) => ({ ...c, accounting_notes: [...(c.accounting_notes || []), data] }));
      setNoteText("");
      onUpdated && onUpdated();
      // Notify sidebar of the counterpart role too (they now have a new unread)
      window.dispatchEvent(new CustomEvent("accounting-changed"));
      window.dispatchEvent(new CustomEvent("cm-accounts-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setSending(false); }
  };

  if (!cardId) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="account-card-drawer">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto w-full max-w-3xl bg-white h-full flex flex-col shadow-2xl relative">
        {loading || !card ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-200 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-teal-700" data-testid="drawer-jc-number">
                  {card.job_card_number}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {card.model || "-"} · {card.store || "-"} · {card.area || "-"}
                </p>
                <div className="flex gap-2 mt-2">
                  {card.completion_report_url && (
                    <>
                      <a
                        data-testid="drawer-print-report"
                        href={`${API_URL}/api/files/${card.completion_report_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs font-medium rounded-lg"
                      >
                        <Printer size={12} /> Print Report
                      </a>
                      <a
                        data-testid="drawer-download-report"
                        href={`${API_URL}/api/files/${card.completion_report_url}`}
                        download={`${card.job_card_number}-completion-report.pdf`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg"
                      >
                        <Download size={12} /> Download
                      </a>
                    </>
                  )}
                </div>
              </div>
              <button
                data-testid="drawer-close"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 bg-slate-50 border-b border-slate-200 grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-slate-400 uppercase tracking-wide text-[10px]">Completed</p>
                <p className="text-slate-700 font-medium">{formatWhen(card.date_completed)}</p>
              </div>
              <div>
                <p className="text-slate-400 uppercase tracking-wide text-[10px]">Collector</p>
                <p className="text-slate-700 font-medium">{card.collector || "-"}</p>
              </div>
              <div>
                <p className="text-slate-400 uppercase tracking-wide text-[10px]">Accountant Status</p>
                {boardKey === "accounting" && availableStatuses.length > 0 ? (
                  <select
                    data-testid="drawer-accounting-status-select"
                    value={card.accounting_status || ""}
                    onChange={(e) => changeStatus(e.target.value)}
                    disabled={savingStatus}
                    className="mt-0.5 w-full h-7 border border-slate-300 rounded-md px-2 text-xs bg-white text-slate-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  >
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-slate-700 font-medium">{card.accounting_status || "-"}</p>
                )}
              </div>
              <div>
                <p className="text-slate-400 uppercase tracking-wide text-[10px]">CM Status</p>
                {boardKey === "cm_accounts" && availableStatuses.length > 0 ? (
                  <select
                    data-testid="drawer-cm-status-select"
                    value={card.cm_status || ""}
                    onChange={(e) => changeStatus(e.target.value)}
                    disabled={savingStatus}
                    className="mt-0.5 w-full h-7 border border-slate-300 rounded-md px-2 text-xs bg-white text-slate-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  >
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-slate-700 font-medium">{card.cm_status || "-"}</p>
                )}
              </div>
            </div>

            {/* Collapsible Completion Report preview */}
            {card.completion_report_url && (
              <div className="border-b border-slate-200 bg-slate-100">
                <button
                  data-testid="drawer-toggle-preview"
                  onClick={() => setPreviewOpen((v) => !v)}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-200/60 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Eye size={13} className="text-teal-600" />
                    <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                      Completion Report Preview
                    </span>
                    {!previewOpen && (
                      <span className="text-[10px] text-slate-400">(click to view)</span>
                    )}
                  </div>
                  {previewOpen
                    ? <ChevronUp size={14} className="text-slate-500" />
                    : <ChevronDown size={14} className="text-slate-500" />}
                </button>
                {previewOpen && (
                  <div className="bg-white border-t border-slate-200">
                    {previewError ? (
                      <div className="p-6 text-center text-sm text-slate-600">
                        PDF preview failed: {previewError}.
                        <a
                          href={`${API_URL}/api/files/${card.completion_report_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 underline text-teal-600"
                        >Open in new tab</a>
                      </div>
                    ) : previewBlobUrl ? (
                      <iframe
                        data-testid="drawer-report-preview"
                        src={`${previewBlobUrl}#view=FitH`}
                        title="Completion Report"
                        className="w-full"
                        style={{ height: "520px" }}
                      />
                    ) : (
                      <div className="p-6 text-center text-sm text-slate-400">Loading PDF…</div>
                    )}
                    <div className="px-3 py-1.5 text-right border-t border-slate-100">
                      <button
                        data-testid="drawer-toggle-preview-close"
                        onClick={() => setPreviewOpen(false)}
                        className="text-[10px] text-slate-500 hover:text-teal-600 inline-flex items-center gap-1"
                      >
                        <ChevronUp size={11} /> Minimize
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="drawer-notes">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Notes Timeline</h3>
              {(card.accounting_notes || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No notes yet. Start the conversation below.</p>
              ) : (
                (card.accounting_notes || []).map((n) => {
                  const pill = ROLE_PILL[n.role] || { label: n.role, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                  const isMine = n.role === currentRole;
                  return (
                    <div key={n.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-xl px-3 py-2 ${isMine ? "bg-teal-50 border border-teal-100" : "bg-white border border-slate-200"}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-slate-700">{n.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${pill.cls}`}>{pill.label}</span>
                          <span className="text-[10px] text-slate-400 ml-auto">{formatWhen(n.timestamp)}</span>
                        </div>
                        <p className="text-xs text-slate-700 whitespace-pre-wrap">{n.message}</p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={notesEndRef} />
            </div>

            <div className="p-3 pb-16 border-t border-slate-200 bg-white">
              <Textarea
                data-testid="drawer-note-input"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note to the timeline…"
                rows={2}
                className="text-sm resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitNote();
                }}
              />
              <div className="flex justify-start mt-2">
                <Button
                  data-testid="drawer-send-note"
                  onClick={submitNote}
                  disabled={sending || !noteText.trim()}
                  className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                >
                  <Send size={12} className="mr-1.5" /> {sending ? "Sending…" : "Post Note"}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Tip: Cmd/Ctrl + Enter to post quickly.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
